import React, { useCallback, useRef } from 'react';
import { ChevronRight, Plus } from 'lucide-react';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';
import {
  SLOT_COMPONENT_GROUPS,
  GROUPS_WITH_SUBGROUPS,
} from '../../constants/slot-asset-components';
import { SlotComponentCard } from './SlotComponentCard';
import type { SlotComponentDef } from '../../../core/types/slot-asset-generator.types';

export const ComponentLibrary: React.FC = () => {
  const components = useSlotAssetGeneratorStore((s) => s.components);
  const selectedComponentId = useSlotAssetGeneratorStore((s) => s.selectedComponentId);
  const expandedGroups = useSlotAssetGeneratorStore((s) => s.expandedGroups);
  const leftPanelLocked = useSlotAssetGeneratorStore((s) => s.leftPanelLocked);
  const toggleGroup = useSlotAssetGeneratorStore((s) => s.toggleGroup);
  const selectComponent = useSlotAssetGeneratorStore((s) => s.selectComponent);
  const addCustomComponent = useSlotAssetGeneratorStore((s) => s.addCustomComponent);

  const listRef = useRef<HTMLDivElement>(null);

  const getGroupComponents = useCallback(
    (group: string, subgroup?: string) => {
      return components.filter(
        (c) => c.group === group && (subgroup ? c.subgroup === subgroup : true)
      );
    },
    [components]
  );

  const getGroupCount = useCallback(
    (group: string) => components.filter((c) => c.group === group).length,
    [components]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && e.key !== 'Enter') return;
      e.preventDefault();

      const visibleComponents = components.filter((c) => expandedGroups.has(c.group));
      if (visibleComponents.length === 0) return;

      const currentIdx = visibleComponents.findIndex((c) => c.id === selectedComponentId);

      if (e.key === 'ArrowDown') {
        const nextIdx = currentIdx < visibleComponents.length - 1 ? currentIdx + 1 : 0;
        selectComponent(visibleComponents[nextIdx].id);
      } else if (e.key === 'ArrowUp') {
        const prevIdx = currentIdx > 0 ? currentIdx - 1 : visibleComponents.length - 1;
        selectComponent(visibleComponents[prevIdx].id);
      }
    },
    [components, expandedGroups, selectedComponentId, selectComponent]
  );

  const handleAddCustom = useCallback(() => {
    addCustomComponent({ name: 'New Component' });
    // Scroll to bottom to show the newly added component
    requestAnimationFrame(() => {
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    });
  }, [addCustomComponent]);

  const renderGroupHeader = (
    groupKey: string,
    label: string,
    count: number,
    indent = false
  ) => {
    const isExpanded = expandedGroups.has(groupKey);
    return (
      <button
        key={`header-${groupKey}`}
        onClick={() => toggleGroup(groupKey)}
        className={`
          w-full flex items-center gap-1.5 py-1.5 px-2 text-xs font-semibold
          text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50 transition-colors
          ${indent ? 'pl-6' : ''}
        `}
      >
        <ChevronRight
          size={12}
          className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}
        />
        <span className="uppercase tracking-wider">{label}</span>
        <span className="ml-auto text-zinc-600 text-[10px] font-normal">({count})</span>
      </button>
    );
  };

  const renderCards = (comps: SlotComponentDef[]) =>
    comps.map((c) => (
      <div key={c.id} className="px-2 py-0.5">
        <SlotComponentCard
          component={c}
          isSelected={c.id === selectedComponentId}
        />
      </div>
    ));

  const renderGroup = (groupMeta: typeof SLOT_COMPONENT_GROUPS[0]) => {
    const groupId = groupMeta.id;
    const isExpanded = expandedGroups.has(groupId);
    const count = getGroupCount(groupId);
    const subgroupDefs = GROUPS_WITH_SUBGROUPS[groupId];

    return (
      <div key={groupId}>
        {renderGroupHeader(groupId, groupMeta.label, count)}
        {isExpanded && (
          <div>
            {subgroupDefs ? (
              Object.entries(subgroupDefs).map(([subKey, subLabel]) => {
                const subGroupKey = `${groupId}:${subKey}`;
                const subComps = getGroupComponents(groupId, subKey);
                if (subComps.length === 0) return null;
                const subExpanded = expandedGroups.has(subGroupKey);
                return (
                  <div key={subGroupKey}>
                    {renderGroupHeader(subGroupKey, subLabel, subComps.length, true)}
                    {subExpanded && renderCards(subComps)}
                  </div>
                );
              })
            ) : (
              renderCards(getGroupComponents(groupId))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`flex-1 overflow-y-auto ${leftPanelLocked ? 'opacity-50 pointer-events-none' : ''}`}
      ref={listRef}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {SLOT_COMPONENT_GROUPS.map(renderGroup)}

      <div className="p-2 border-t border-zinc-800">
        <button
          onClick={handleAddCustom}
          className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-zinc-400 hover:text-zinc-200 border border-dashed border-zinc-700 hover:border-zinc-500 rounded transition-colors"
        >
          <Plus size={12} />
          Add Custom Component
        </button>
      </div>
    </div>
  );
};
